import { CSSProperties, ReactNode } from "react";
import classNames from "classnames";

import styles from "./style.module.scss";

export const SCENE_VIEWBOX = "0 0 240 200";

interface SceneProps {
  label: string;
  className?: string;
  children: ReactNode;
}

export const Scene = ({ label, className, children }: SceneProps) => (
  <svg
    viewBox={SCENE_VIEWBOX}
    role="img"
    aria-label={label}
    className={classNames(styles.scene, className)}
  >
    {children}
  </svg>
);

// Drawn at the origin; scenes move it with transform keyframes.
export const Cursor = ({
  className,
  style,
}: {
  className: string;
  style?: CSSProperties;
}) => (
  <path
    className={classNames("ga-cursor", className)}
    style={style}
    d="M0 0L0 14.5L4 11L7 17.5L9.5 16.5L6.5 10L11 10Z"
  />
);

interface ActionButtonProps {
  x: number;
  y: number;
  tone: "success" | "primary" | "warning" | "error";
  icon: "check" | "tag" | "ignore" | "x";
  className?: string;
}

const ICONS: Record<ActionButtonProps["icon"], ReactNode> = {
  check: <path className="ga-icon" d="M7 12.5l3.2 3.2L17 9" />,
  tag: (
    <>
      <path className="ga-icon" d="M7 7h5l5 5-5 5-5-5V7Z" />
      <circle className="ga-icon-dot" cx="10" cy="10" r="1.2" />
    </>
  ),
  ignore: (
    <>
      <circle className="ga-icon" cx="12" cy="12" r="5.5" />
      <path className="ga-icon" d="M8.2 15.8l7.6-7.6" />
    </>
  ),
  x: <path className="ga-icon" d="M8 8l8 8M16 8l-8 8" />,
};

// Mirrors the floating review actions: approve, reassign, ignore, decline.
export const ActionButton = ({
  x,
  y,
  tone,
  icon,
  className,
}: ActionButtonProps) => (
  <g transform={`translate(${x} ${y})`} className={className}>
    <rect className={`ga-btn ga-btn-${tone}`} width="24" height="24" rx="6" />
    {ICONS[icon]}
  </g>
);

interface TileProps {
  x: number;
  y: number;
  size?: number;
  className?: string;
}

// A thumbnail stand-in: just an empty rounded box.
export const Tile = ({ x, y, size = 56, className }: TileProps) => (
  <rect
    className={classNames("ga-tile", className)}
    x={x}
    y={y}
    width={size}
    height={size}
    rx={size * 0.16}
  />
);

interface BadgeProps {
  x: number;
  y: number;
  width: number;
  text: string;
  tone: "warning" | "success" | "error" | "muted" | "primary";
  className?: string;
}

export const badgeWidth = (text: string) => Math.round(text.length * 5 + 18);

export const Badge = ({ x, y, width, text, tone, className }: BadgeProps) => {
  const w = Math.max(width, badgeWidth(text));
  return (
    <g transform={`translate(${x} ${y})`} className={className}>
      <rect
        className={`ga-badge ga-badge-${tone}`}
        width={w}
        height="16"
        rx="8"
      />
      <text className="ga-badge-text" x={w / 2} y="11" textAnchor="middle">
        {text}
      </text>
    </g>
  );
};

interface StatusPillProps {
  x: number;
  y: number;
  text: string;
  tone: "success" | "primary" | "warning" | "error" | "muted";
  spinner?: boolean;
  className?: string;
}

// Task Manager style status: tinted pill, colored dot (or spinner) and colored text.
export const StatusPill = ({
  x,
  y,
  text,
  tone,
  spinner,
  className,
}: StatusPillProps) => {
  const w = badgeWidth(text) + 12;
  return (
    <g transform={`translate(${x} ${y})`} className={className}>
      <rect
        className={`ga-pill ga-pill-${tone}`}
        width={w}
        height="18"
        rx="9"
      />
      {spinner ? (
        <circle
          className={`ga-pill-spinner ga-pill-dot-${tone}`}
          cx="11"
          cy="9"
          r="3"
          strokeDasharray="10 8"
        />
      ) : (
        <circle
          className={`ga-pill-dot ga-pill-dot-${tone}`}
          cx="11"
          cy="9"
          r="2.5"
        />
      )}
      <text className={`ga-pill-text ga-pill-text-${tone}`} x="19" y="12">
        {text}
      </text>
    </g>
  );
};
